import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Container,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import HospitalLogo from './HospitalLogo';
import PageTransitionLoader from './PageTransitionLoader';
import { PageReadyProvider, usePageReady } from '../../context/PageReadyContext';
import { getApiBaseUrl } from '../../services/api';
import { useData } from '../../context/DataContext';
import { slugify } from '../../utils/slugify';
import {
  Info as InfoIcon,
  Visibility as VisionIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  MedicalServices as MedicalIcon,
  Healing as SurgicalIcon,
  Science as DiagnosticIcon,
  LocalHospital as EmergencyIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import BiotechIcon from '@mui/icons-material/Biotech';

const PublicLayoutContent = () => {
  const { state } = useData();
  const { hospitalInfo, departments } = state;
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  // MUI Menu anchor elements for all dropdowns
  const [aboutAnchorEl, setAboutAnchorEl] = useState(null);
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null);
  const [newServicesAnchorEl, setNewServicesAnchorEl] = useState(null);
  const [surgicalSubmenuAnchorEl, setSurgicalSubmenuAnchorEl] = useState(null);
  const [medicalSubmenuAnchorEl, setMedicalSubmenuAnchorEl] = useState(null);
  const [opdSubmenuAnchorEl, setOpdSubmenuAnchorEl] = useState(null);
  const [ipdSubmenuAnchorEl, setIpdSubmenuAnchorEl] = useState(null);
  const [radiologySubmenuAnchorEl, setRadiologySubmenuAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileSurgicalOpen, setMobileSurgicalOpen] = useState(false);
  const [mobileMedicalOpen, setMobileMedicalOpen] = useState(false);
  const [mobileNewServicesOpen, setMobileNewServicesOpen] = useState(false);
  const [mobileOpdOpen, setMobileOpdOpen] = useState(false);
  const [mobileIpdOpen, setMobileIpdOpen] = useState(false);
  const [mobileRadiologyOpen, setMobileRadiologyOpen] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [checkingMaintenance, setCheckingMaintenance] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const isInitialRoute = React.useRef(true);
  const { pageReady } = usePageReady();

  // Timeout refs for hover menus
  const menuTimeoutRef = React.useRef(null);
  const MENU_DELAY = 300; // Increased delay for smoother hover experience

  // Check maintenance status early on layout mount
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await fetch(`${getApiBaseUrl()}/maintenance`);
        const json = await resp.json();
        if (mounted && json && json.success) {
          setMaintenance(Boolean(json.data?.maintenance));
        }
      } catch (e) {
        // fail open
      } finally {
        if (mounted) setCheckingMaintenance(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  React.useEffect(() => {
    if (!checkingMaintenance && maintenance) {
      if (location.pathname !== '/maintenance') {
        navigate('/maintenance', { replace: true });
      }
    }
  }, [checkingMaintenance, maintenance, location.pathname, navigate]);

  React.useEffect(() => {
    if (isInitialRoute.current) {
      isInitialRoute.current = false;
      return undefined;
    }
    setPageLoading(true);
    return undefined;
  }, [location.pathname]);

  React.useEffect(() => {
    if (pageLoading && pageReady) {
      setPageLoading(false);
    }
  }, [pageLoading, pageReady]);

  React.useEffect(() => {
    document.body.style.overflow = pageLoading ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [pageLoading]);

  // Scroll to top instantly when route changes (before paint)
  React.useLayoutEffect(() => {
    // Disable browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Disable smooth scroll behavior for instant scroll on route change
    const html = document.documentElement;
    const body = document.body;
    
    // Override smooth scroll with instant scroll
    html.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';
    
    // Immediately set scroll position to top (synchronous)
    // Use multiple methods to ensure it works across all browsers
    html.scrollTop = 0;
    body.scrollTop = 0;
    window.scrollTo(0, 0);
    window.scroll(0, 0);
    
    // Force a reflow to ensure scroll happens before paint
    void html.offsetHeight;
    
    // Use requestAnimationFrame to restore smooth scroll after scroll completes
    // This ensures the instant scroll happens before smooth scroll is re-enabled
    requestAnimationFrame(() => {
      html.style.scrollBehavior = '';
      body.style.scrollBehavior = '';
    });
  }, [location.pathname]);
  
  const navItems = [
    { name: 'Home', path: '/' },
    // { name: 'Doctors', path: '/doctors' },
  ];

  const aboutMenuItems = [
    { name: 'Overview', path: '/about', icon: <InfoIcon /> },
    { name: 'Vision & Mission', path: '/about/vision-mission', icon: <VisionIcon /> },
    { name: 'CEO Message', path: '/about/ceo-message', icon: <PersonIcon /> },
    { name: 'Board of Directors', path: '/about/board', icon: <GroupIcon /> },
    { name: 'Our Team', path: '/about/team', icon: <PeopleIcon /> },
  ];

  // Filter departments by category
  const surgicalDepartments = departments.filter(dept => 
    dept.service_category === 'Surgical Clinics' && 
    (dept.is_active === true || dept.is_active === 1)
  );
  
  const medicalDepartments = departments.filter(dept => 
    dept.service_category === 'Medical Clinics' && 
    (dept.is_active === true || dept.is_active === 1)
  );

  const servicesMenuItems = [
    { 
      name: 'Medical Clinics', 
      path: '/services/medical-clinics', 
      icon: <MedicalIcon />,
      hasSubmenu: true,
      submenuItems: medicalDepartments
    },
    { 
      name: 'Surgical Clinics', 
      path: '/services/surgical-clinics', 
      icon: <SurgicalIcon />,
      hasSubmenu: true,
      submenuItems: surgicalDepartments
    },
  ];

  const newServicesMenuItems = [
    {
      name: 'OPD Services',
      path: null,
      icon: <MedicalIcon />,
      hasSubmenu: true,
      submenuItems: [
        { name: 'Endoscopy/ERCP', path: '/services/endoscopy-ercp' },
        { name: 'Echo/ETT', path: '/services/echo-ett' },
        { name: 'Dialysis', path: '/services/dialysis' },
        { name: 'Daycare', path: '/services/daycare' },
      ]
    },
    {
      name: 'IPD Services',
      path: null,
      icon: <SurgicalIcon />,
      hasSubmenu: true,
      submenuItems: [
        { name: 'Admission', path: '/services/admission' },
        { name: 'Rooms & Wards', path: '/services/rooms-wards' },
        { name: 'Medical ICU', path: '/services/medical-icu' },
        { name: 'Surgical ICU', path: '/services/surgical-icu' },
        { name: 'Neonatal ICU', path: '/services/neonatal-icu' },
        { name: 'Coronary Care Unit', path: '/services/coronary-care-unit' },
        { name: 'High Dependency Unit', path: '/services/high-dependency-unit' },
        { name: 'Operation Theater', path: '/services/operation-theater' },
        { name: 'Interventional Cardiology', path: '/services/interventional-cardiology' },
      ]
    },
    {
      name: 'Radiology',
      path: '/services/radiology',
      icon: <DiagnosticIcon />,
      hasSubmenu: true,
      submenuItems: [
        { name: 'Diagnostic radiology', path: '/services/radiology' },
        { name: 'Interventional radiology', path: '/services/interventional-radiology' },
      ]
    },
    {
      name: 'Pathology Services',
      path: '/services/pathology-services',
      icon: <BiotechIcon />,
      hasSubmenu: false
    },
    {
      name: 'Emergency',
      path: '/services/emergency-services',
      icon: <EmergencyIcon />,
      hasSubmenu: false
    },
   
  ];

  const DEPARTMENT_NAV_PATHS = ['/services/medical-clinics', '/services/surgical-clinics'];

  const isHomeNavActive = () => location.pathname === '/';

  const isDepartmentsNavActive = () => {
    const path = location.pathname;
    if (path === '/departments' || path.startsWith('/department/')) return true;
    return DEPARTMENT_NAV_PATHS.some(
      (p) => path === p || path.startsWith(`${p}/`)
    );
  };

  const isServicesNavActive = () => {
    const path = location.pathname;
    if (!path.startsWith('/services')) return false;
    return !DEPARTMENT_NAV_PATHS.some(
      (p) => path === p || path.startsWith(`${p}/`)
    );
  };

  const isAboutNavActive = () => {
    const path = location.pathname;
    return path === '/about' || path.startsWith('/about/');
  };

  const isCareersNavActive = () =>
    location.pathname === '/careers' || location.pathname.startsWith('/careers/');

  const isContactNavActive = () => location.pathname === '/contact';

  const getDesktopNavButtonSx = (isActive) => ({
    color: isActive ? '#1d4ed8' : 'rgba(255, 255, 255, 0.92)',
    textTransform: 'none',
    fontWeight: isActive ? 700 : 500,
    fontSize: '0.9375rem',
    letterSpacing: '0.01em',
    px: 2.25,
    py: 1,
    borderRadius: '9999px',
    minHeight: 40,
    backgroundColor: isActive ? '#ffffff' : 'transparent',
    boxShadow: isActive
      ? '0 2px 10px rgba(15, 23, 42, 0.18), inset 0 0 0 1px rgba(255, 255, 255, 0.8)'
      : 'none',
    transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      backgroundColor: isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.14)',
      color: isActive ? '#1d4ed8' : '#ffffff',
    },
    '& .MuiButton-endIcon': {
      color: 'inherit',
      transition: 'transform 0.2s ease',
    },
  });

  const getMobileNavItemSx = (isActive) => ({
    borderLeft: isActive ? '3px solid #2563eb' : '3px solid transparent',
    backgroundColor: isActive ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
    '&:hover': {
      backgroundColor: isActive ? 'rgba(37, 99, 235, 0.14)' : 'rgba(37, 99, 235, 0.05)',
    },
  });

  const getMobileNavTextSx = (isActive) => ({
    '& .MuiTypography-root': {
      color: isActive ? '#1e40af' : 'inherit',
      fontWeight: isActive ? 600 : 400,
    },
  });

  // Helper function to close all desktop menus
  const closeAllMenus = () => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
    setAboutAnchorEl(null);
    setServicesAnchorEl(null);
    setNewServicesAnchorEl(null);
    setSurgicalSubmenuAnchorEl(null);
    setMedicalSubmenuAnchorEl(null);
    setOpdSubmenuAnchorEl(null);
    setIpdSubmenuAnchorEl(null);
    setRadiologySubmenuAnchorEl(null);
  };

  // Clear timeout helper
  const clearMenuTimeout = () => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
  };

  // === DEPARTMENTS (Services) Menu Handlers ===
  const handleServicesMouseEnter = (event) => {
    if (!isMobile) {
      clearMenuTimeout();
      // Close other main menus
      setAboutAnchorEl(null);
      setNewServicesAnchorEl(null);
      setOpdSubmenuAnchorEl(null);
      setIpdSubmenuAnchorEl(null);
      setRadiologySubmenuAnchorEl(null);
      // Open this menu
      setServicesAnchorEl(event.currentTarget);
    }
  };

  const handleServicesMenuEnter = () => {
    clearMenuTimeout();
  };

  const handleServicesMenuLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setServicesAnchorEl(null);
      setSurgicalSubmenuAnchorEl(null);
      setMedicalSubmenuAnchorEl(null);
    }, MENU_DELAY);
  };

  const handleServicesClose = () => {
    clearMenuTimeout();
    setServicesAnchorEl(null);
    setSurgicalSubmenuAnchorEl(null);
    setMedicalSubmenuAnchorEl(null);
  };

  const handleServicesItemClick = (path) => {
    closeAllMenus();
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
    setMobileSurgicalOpen(false);
    setMobileMedicalOpen(false);
    if (path) {
      navigate(path);
    }
  };

  // === SERVICES (NewServices) Menu Handlers ===
  const handleNewServicesMouseEnter = (event) => {
    if (!isMobile) {
      clearMenuTimeout();
      // Close other main menus
      setAboutAnchorEl(null);
      setServicesAnchorEl(null);
      setSurgicalSubmenuAnchorEl(null);
      setMedicalSubmenuAnchorEl(null);
      // Open this menu
      setNewServicesAnchorEl(event.currentTarget);
    }
  };

  const handleNewServicesMenuEnter = () => {
    clearMenuTimeout();
  };

  const handleNewServicesMenuLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setNewServicesAnchorEl(null);
      setOpdSubmenuAnchorEl(null);
      setIpdSubmenuAnchorEl(null);
      setRadiologySubmenuAnchorEl(null);
    }, MENU_DELAY);
  };

  const handleNewServicesClose = () => {
    clearMenuTimeout();
    setNewServicesAnchorEl(null);
    setOpdSubmenuAnchorEl(null);
    setIpdSubmenuAnchorEl(null);
    setRadiologySubmenuAnchorEl(null);
  };

  const handleNewServicesItemClick = (path) => {
    closeAllMenus();
    setMobileMenuOpen(false);
    setMobileNewServicesOpen(false);
    setMobileOpdOpen(false);
    setMobileIpdOpen(false);
    setMobileRadiologyOpen(false);
    if (path) {
      navigate(path);
    }
  };

  // === ABOUT Menu Handlers ===
  const handleAboutMouseEnter = (event) => {
    if (!isMobile) {
      clearMenuTimeout();
      // Close other main menus
      setServicesAnchorEl(null);
      setSurgicalSubmenuAnchorEl(null);
      setMedicalSubmenuAnchorEl(null);
      setNewServicesAnchorEl(null);
      setOpdSubmenuAnchorEl(null);
      setIpdSubmenuAnchorEl(null);
      setRadiologySubmenuAnchorEl(null);
      // Open this menu
      setAboutAnchorEl(event.currentTarget);
    }
  };

  const handleAboutMenuEnter = () => {
    clearMenuTimeout();
  };

  const handleAboutMenuLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setAboutAnchorEl(null);
    }, MENU_DELAY);
  };

  const handleAboutClose = () => {
    clearMenuTimeout();
    setAboutAnchorEl(null);
  };

  const handleAboutItemClick = (path) => {
    closeAllMenus();
    setMobileMenuOpen(false);
    setMobileAboutOpen(false);
  };

  // === Submenu Handlers ===
  const handleSurgicalMouseEnter = (event) => {
    if (!isMobile) {
      clearMenuTimeout();
      setMedicalSubmenuAnchorEl(null);
      setSurgicalSubmenuAnchorEl(event.currentTarget);
    }
  };

  const handleSurgicalClose = () => {
    setSurgicalSubmenuAnchorEl(null);
  };

  const handleMedicalMouseEnter = (event) => {
    if (!isMobile) {
      clearMenuTimeout();
      setSurgicalSubmenuAnchorEl(null);
      setMedicalSubmenuAnchorEl(event.currentTarget);
    }
  };

  const handleMedicalClose = () => {
    setMedicalSubmenuAnchorEl(null);
  };

  const handleDepartmentClick = (department) => {
    navigate(`/department/${slugify(department.name)}`);
    handleServicesItemClick();
  };

  const handleOpdMouseEnter = (event) => {
    if (!isMobile) {
      clearMenuTimeout();
      setIpdSubmenuAnchorEl(null);
      setRadiologySubmenuAnchorEl(null);
      setOpdSubmenuAnchorEl(event.currentTarget);
    }
  };

  const handleOpdClose = () => {
    setOpdSubmenuAnchorEl(null);
  };

  const handleIpdMouseEnter = (event) => {
    if (!isMobile) {
      clearMenuTimeout();
      setOpdSubmenuAnchorEl(null);
      setRadiologySubmenuAnchorEl(null);
      setIpdSubmenuAnchorEl(event.currentTarget);
    }
  };

  const handleIpdClose = () => {
    setIpdSubmenuAnchorEl(null);
  };

  const handleRadiologyMouseEnter = (event) => {
    if (!isMobile) {
      clearMenuTimeout();
      setOpdSubmenuAnchorEl(null);
      setIpdSubmenuAnchorEl(null);
      setRadiologySubmenuAnchorEl(event.currentTarget);
    }
  };

  const handleRadiologyClose = () => {
    setRadiologySubmenuAnchorEl(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileServicesToggle = () => {
    setMobileServicesOpen(!mobileServicesOpen);
  };

  const handleMobileAboutToggle = () => {
    setMobileAboutOpen(!mobileAboutOpen);
  };

  const handleMobileNewServicesToggle = () => {
    setMobileNewServicesOpen(!mobileNewServicesOpen);
  };

  const handleMobileItemClick = () => {
    setMobileMenuOpen(false);
    setMobileServicesOpen(false);
    setMobileAboutOpen(false);
    setMobileSurgicalOpen(false);
    setMobileMedicalOpen(false);
    setMobileNewServicesOpen(false);
    setMobileOpdOpen(false);
    setMobileIpdOpen(false);
    setMobileRadiologyOpen(false);
  };

  // Mobile drawer content
  const mobileDrawer = (
    <Box sx={{ width: 280, pt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e40af' }}>
          Menu
        </Typography>
        <IconButton onClick={handleMobileMenuToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ pt: 1 }}>
        {/* Home */}
        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to="/"
            onClick={handleMobileItemClick}
            sx={getMobileNavItemSx(isHomeNavActive())}
          >
            <ListItemText 
              primary="Home" 
              sx={getMobileNavTextSx(isHomeNavActive())}
            />
          </ListItemButton>
        </ListItem>

        {/* Departments - Hidden but page still accessible */}
        {/* <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to="/departments"
            onClick={handleMobileItemClick}
            sx={{
              backgroundColor: location.pathname === '/departments' ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: location.pathname === '/departments' ? 'rgba(37, 99, 235, 0.15)' : 'rgba(37, 99, 235, 0.05)',
              }
            }}
          >
            <ListItemText 
              primary="Departments" 
              sx={{ 
                color: location.pathname === '/departments' ? '#1e40af' : 'inherit',
                fontWeight: location.pathname === '/departments' ? 600 : 400,
              }} 
            />
          </ListItemButton>
        </ListItem> */}

        {/* Doctors */}
        {/* <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to="/doctors"
            onClick={handleMobileItemClick}
            sx={{
              backgroundColor: location.pathname === '/doctors' ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: location.pathname === '/doctors' ? 'rgba(37, 99, 235, 0.15)' : 'rgba(37, 99, 235, 0.05)',
              }
            }}
          >
            <ListItemText 
              primary="Doctors" 
              sx={{ 
                color: location.pathname === '/doctors' ? '#1e40af' : 'inherit',
                fontWeight: location.pathname === '/doctors' ? 600 : 400,
              }} 
            />
          </ListItemButton>
        </ListItem> */}

        {/* Services */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleMobileServicesToggle}
            sx={getMobileNavItemSx(isDepartmentsNavActive())}
          >
            <ListItemText 
              primary="Departments" 
              sx={getMobileNavTextSx(isDepartmentsNavActive())}
            />
            <ExpandMoreIcon 
              sx={{ 
                transform: mobileServicesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease'
              }} 
            />
          </ListItemButton>
        </ListItem>

        {/* Services Submenu */}
        {mobileServicesOpen && (
          <List component="div" disablePadding>
            {servicesMenuItems.map((item) => (
              <React.Fragment key={item.name}>
                <ListItem disablePadding>
                  <ListItemButton
                    component={item.hasSubmenu ? 'div' : RouterLink}
                    to={item.hasSubmenu ? undefined : item.path}
                    onClick={() => {
                      if (item.name === 'Surgical Clinics' && item.hasSubmenu) {
                        setMobileSurgicalOpen(!mobileSurgicalOpen);
                      } else if (item.name === 'Medical Clinics' && item.hasSubmenu) {
                        setMobileMedicalOpen(!mobileMedicalOpen);
                      } else if (!item.hasSubmenu) {
                        handleServicesItemClick();
                      }
                    }}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.name} 
                      sx={{ 
                        color: location.pathname === item.path ? '#1e40af' : 'inherit',
                        fontWeight: location.pathname === item.path ? 600 : 400,
                      }} 
                    />
                    {item.hasSubmenu && (
                      <ExpandMoreIcon 
                        sx={{ 
                          transform: (item.name === 'Surgical Clinics' && mobileSurgicalOpen) || 
                                    (item.name === 'Medical Clinics' && mobileMedicalOpen) 
                                    ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease'
                        }} 
                      />
                    )}
                  </ListItemButton>
                </ListItem>
                
                {/* Nested Submenu for Surgical Clinics */}
                {item.name === 'Surgical Clinics' && item.hasSubmenu && mobileSurgicalOpen && (
                  <List component="div" disablePadding>
                    {surgicalDepartments.length > 0 ? (
                      surgicalDepartments.map((dept) => (
                        <ListItem key={dept.id} disablePadding>
                          <ListItemButton
                            onClick={() => {
                              handleDepartmentClick(dept);
                              setMobileSurgicalOpen(false);
                            }}
                            sx={{ pl: 6 }}
                          >
                            <ListItemText primary={dept.name} />
                          </ListItemButton>
                        </ListItem>
                      ))
                    ) : (
                      <ListItem disablePadding>
                        <ListItemButton disabled sx={{ pl: 6 }}>
                          <ListItemText primary="No surgical clinics available" />
                        </ListItemButton>
                      </ListItem>
                    )}
                  </List>
                )}

                {/* Nested Submenu for Medical Clinics */}
                {item.name === 'Medical Clinics' && item.hasSubmenu && mobileMedicalOpen && (
                  <List component="div" disablePadding>
                    {medicalDepartments.length > 0 ? (
                      medicalDepartments.map((dept) => (
                        <ListItem key={dept.id} disablePadding>
                          <ListItemButton
                            onClick={() => {
                              handleDepartmentClick(dept);
                              setMobileMedicalOpen(false);
                            }}
                            sx={{ pl: 6 }}
                          >
                            <ListItemText primary={dept.name} />
                          </ListItemButton>
                        </ListItem>
                      ))
                    ) : (
                      <ListItem disablePadding>
                        <ListItemButton disabled sx={{ pl: 6 }}>
                          <ListItemText primary="No medical clinics available" />
                        </ListItemButton>
                      </ListItem>
                    )}
                  </List>
                )}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Services */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleMobileNewServicesToggle}
            sx={getMobileNavItemSx(isServicesNavActive())}
          >
            <ListItemText 
              primary="Services" 
              sx={getMobileNavTextSx(isServicesNavActive())}
            />
            <ExpandMoreIcon 
              sx={{ 
                transform: mobileNewServicesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease'
              }} 
            />
          </ListItemButton>
        </ListItem>

        {/* Services Submenu */}
        {mobileNewServicesOpen && (
          <List component="div" disablePadding>
            {newServicesMenuItems.map((item) => (
              <React.Fragment key={item.name}>
                <ListItem disablePadding>
                  <ListItemButton
                    component={item.hasSubmenu ? 'div' : (item.path ? RouterLink : 'div')}
                    to={item.hasSubmenu ? undefined : (item.path || undefined)}
                    onClick={() => {
                      if (item.name === 'OPD Services' && item.hasSubmenu) {
                        setMobileOpdOpen(!mobileOpdOpen);
                      } else if (item.name === 'IPD Services' && item.hasSubmenu) {
                        setMobileIpdOpen(!mobileIpdOpen);
                      } else if (item.name === 'Radiology' && item.hasSubmenu) {
                        setMobileRadiologyOpen(!mobileRadiologyOpen);
                      } else if (!item.hasSubmenu) {
                        handleNewServicesItemClick(item.path);
                        if (item.path) {
                          navigate(item.path);
                        }
                      }
                    }}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.name} 
                      sx={{ 
                        color: 'inherit',
                        fontWeight: 400,
                      }} 
                    />
                    {item.hasSubmenu && (
                      <ExpandMoreIcon 
                        sx={{ 
                          transform: (item.name === 'OPD Services' && mobileOpdOpen) || 
                                    (item.name === 'IPD Services' && mobileIpdOpen) ||
                                    (item.name === 'Radiology' && mobileRadiologyOpen)
                                    ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease'
                        }} 
                      />
                    )}
                  </ListItemButton>
                </ListItem>
                
                {/* Nested Submenu for OPD Services */}
                {item.name === 'OPD Services' && item.hasSubmenu && mobileOpdOpen && (
                  <List component="div" disablePadding>
                    {item.submenuItems.map((subItem) => (
                      <ListItem key={subItem.name} disablePadding>
                        <ListItemButton
                          onClick={() => {
                            handleNewServicesItemClick();
                            if (subItem.path) {
                              navigate(subItem.path);
                            }
                          }}
                          sx={{ pl: 6 }}
                        >
                          <ListItemText primary={subItem.name} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}

                {/* Nested Submenu for IPD Services */}
                {item.name === 'IPD Services' && item.hasSubmenu && mobileIpdOpen && (
                  <List component="div" disablePadding>
                    {item.submenuItems.map((subItem) => (
                      <ListItem key={subItem.name} disablePadding>
                        <ListItemButton
                          onClick={() => {
                            handleNewServicesItemClick();
                            if (subItem.path) {
                              navigate(subItem.path);
                            }
                          }}
                          sx={{ pl: 6 }}
                        >
                          <ListItemText primary={subItem.name} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}

                {/* Nested Submenu for Radiology */}
                {item.name === 'Radiology' && item.hasSubmenu && mobileRadiologyOpen && (
                  <List component="div" disablePadding>
                    {item.submenuItems.map((subItem) => (
                      <ListItem key={subItem.name} disablePadding>
                        <ListItemButton
                          component={subItem.path ? RouterLink : 'div'}
                          to={subItem.path || undefined}
                          onClick={() => {
                            handleNewServicesItemClick(subItem.path);
                          }}
                          sx={{ pl: 6 }}
                        >
                          <ListItemText primary={subItem.name} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* About Us */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleMobileAboutToggle}
            sx={getMobileNavItemSx(isAboutNavActive())}
          >
            <ListItemText 
              primary="About Us" 
              sx={getMobileNavTextSx(isAboutNavActive())}
            />
            <ExpandMoreIcon 
              sx={{ 
                transform: mobileAboutOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease'
              }} 
            />
          </ListItemButton>
        </ListItem>

        {/* About Submenu */}
        {mobileAboutOpen && (
          <List component="div" disablePadding>
            {aboutMenuItems.map((item) => (
              <ListItem key={item.name} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  onClick={handleAboutItemClick}
                  sx={{ pl: 4 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.name} 
                    sx={{ 
                      color: location.pathname === item.path ? '#1e40af' : 'inherit',
                      fontWeight: location.pathname === item.path ? 600 : 400,
                    }} 
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {/* Careers */}
        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to="/careers"
            onClick={handleMobileItemClick}
            sx={getMobileNavItemSx(isCareersNavActive())}
          >
            <ListItemText 
              primary="Careers" 
              sx={getMobileNavTextSx(isCareersNavActive())}
            />
          </ListItemButton>
        </ListItem>

        {/* Contact */}
        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to="/contact"
            onClick={handleMobileItemClick}
            sx={getMobileNavItemSx(isContactNavActive())}
          >
            <ListItemText 
              primary="Contact" 
              sx={getMobileNavTextSx(isContactNavActive())}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Top Bar with Social Icons and Contact */}
      <Box sx={{ 
        backgroundColor: 'white',
        color: '#1e293b',
        py: 1,
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        display: { xs: 'none', lg: 'block' }
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 3
          }}>
            {/* Contact Information and Social Media Icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {hospitalInfo.showContact && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {hospitalInfo.contact}
                  </Typography>
                </Box>
              )}
              {hospitalInfo.showEmail && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {hospitalInfo.email}
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* Social Media Icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'rgba(0,0,0,0.05)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#3b82f6',
                  transform: 'translateY(-2px)'
                }
              }}>
                <FacebookIcon sx={{ fontSize: 18, color: '#1e293b' }} />
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'rgba(0,0,0,0.05)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#1da1f2',
                  transform: 'translateY(-2px)'
                }
              }}>
                <TwitterIcon sx={{ fontSize: 18, color: '#1e293b' }} />
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'rgba(0,0,0,0.05)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <InstagramIcon sx={{ fontSize: 18, color: '#1e293b' }} />
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'rgba(0,0,0,0.05)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#0077b5',
                  transform: 'translateY(-2px)'
                }
              }}>
                <LinkedInIcon sx={{ fontSize: 18, color: '#1e293b' }} />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Box sx={{ position: 'relative' }}>
        {/* Logo Box - Positioned to overlay the navigation bar */}
        <Container
          maxWidth="lg"
          disableGutters={isMobile}
          sx={{
            position: 'relative',
            zIndex: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: { xs: 'fixed', lg: 'absolute' },
              top: { xs: 0, lg: -15 },
              left: { xs: 0, lg: 0 },
              right: { xs: 0, lg: 'auto' },
              width: { xs: '100%', lg: 'auto' },
              px: { xs: 2, lg: 0 },
              pt: {
                xs: 'max(12px, env(safe-area-inset-top, 0px))',
                lg: 0,
              },
              pb: { xs: 1, lg: 0 },
              zIndex: { xs: 1101, lg: 3 },
              boxSizing: 'border-box',
            }}
          >
            <HospitalLogo
              size="large"
              variant="header"
              showText={true}
              clickable={true}
              containerStyle={{ minWidth: 0, flex: 1 }}
            />

            {/* Mobile Hamburger Menu */}
            <IconButton
              onClick={handleMobileMenuToggle}
              aria-label="Open navigation menu"
              sx={{
                display: { xs: 'flex', lg: 'none' },
                flexShrink: 0,
                ml: 1,
                color: '#1e40af',
                backgroundColor: 'rgba(255, 255, 255, 0.92)',
                boxShadow: '0px 2px 8px rgba(0,0,0,0.12)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Container>

        <AppBar
          position="static"
          sx={{
            display: { xs: 'none', lg: 'block' },
            background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
            boxShadow: '0px 4px 20px rgba(37, 99, 235, 0.15)',
            backdropFilter: 'blur(10px)',
            width: '100%',
            borderRadius: '0 !important',
            '& .MuiAppBar-root': {
              borderRadius: '0 !important',
            },
          }}
        >
          <Container maxWidth="lg">
            <Toolbar sx={{ 
              py: 1.5, 
              minHeight: '70px', 
              alignItems: 'flex-end', 
              pl: { xs: 0, lg: 25 },
              display: { xs: 'none', lg: 'flex' }
            }}>
              <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                {/* Home */}
                <Button
                  component={RouterLink}
                  to="/"
                  sx={getDesktopNavButtonSx(isHomeNavActive())}
                >
                  Home
                </Button>

                {/* Departments - Hidden but page still accessible */}
                {/* <Button
                  component={RouterLink}
                  to="/departments"
                  sx={{ 
                    color: 'white', 
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: location.pathname === '/departments' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                    boxShadow: location.pathname === '/departments' ? '0px 2px 8px rgba(0, 0, 0, 0.15)' : 'none',
                    '&:hover': {
                      backgroundColor: location.pathname === '/departments' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  Departments
                </Button> */}

                {/* Doctors */}
                {/* <Button
                  component={RouterLink}
                  to="/doctors"
                  sx={{ 
                    color: 'white', 
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: location.pathname === '/doctors' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                    boxShadow: location.pathname === '/doctors' ? '0px 2px 8px rgba(0, 0, 0, 0.15)' : 'none',
                    '&:hover': {
                      backgroundColor: location.pathname === '/doctors' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  Doctors
                </Button> */}

                {/* Departments Dropdown */}
                <Box
                  onMouseEnter={handleServicesMouseEnter}
                  onMouseLeave={handleServicesMenuLeave}
                  sx={{ position: 'relative' }}
                >
                  <Button
                    sx={getDesktopNavButtonSx(isDepartmentsNavActive())}
                    endIcon={<ExpandMoreIcon />}
                  >
                    Departments
                  </Button>
                  
                  <Menu
                    anchorEl={servicesAnchorEl}
                    open={Boolean(servicesAnchorEl)}
                    onClose={handleServicesClose}
                    disableRestoreFocus
                    disableScrollLock
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    MenuListProps={{
                      onMouseEnter: handleServicesMenuEnter,
                      onMouseLeave: handleServicesMenuLeave,
                    }}
                    sx={{
                      pointerEvents: 'none',
                      '& .MuiPaper-root': {
                        pointerEvents: 'auto',
                        mt: 0.5,
                        minWidth: 250,
                        boxShadow: '0px 8px 25px rgba(0, 0, 0, 0.15)',
                        borderRadius: 2,
                      }
                    }}
                  >
                    {servicesMenuItems.map((item) => (
                      <Box
                        key={item.name}
                        onMouseEnter={(e) => {
                          clearMenuTimeout();
                          if (item.name === 'Surgical Clinics' && item.hasSubmenu) {
                            handleSurgicalMouseEnter(e);
                          } else if (item.name === 'Medical Clinics' && item.hasSubmenu) {
                            handleMedicalMouseEnter(e);
                          } else {
                            setSurgicalSubmenuAnchorEl(null);
                            setMedicalSubmenuAnchorEl(null);
                          }
                        }}
                        sx={{ position: 'relative' }}
                      >
                        <MenuItem
                          component={RouterLink}
                          to={item.path}
                          onClick={() => {
                            handleServicesItemClick(item.path);
                          }}
                          sx={{
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                              backgroundColor: 'rgba(37, 99, 235, 0.08)',
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText primary={item.name} />
                          {item.hasSubmenu && (
                            <ChevronRightIcon sx={{ ml: 1, fontSize: 20 }} />
                          )}
                        </MenuItem>
                        
                        {/* Nested Menu for Surgical Clinics */}
                        {item.name === 'Surgical Clinics' && item.hasSubmenu && (
                          <Menu
                            anchorEl={surgicalSubmenuAnchorEl}
                            open={Boolean(surgicalSubmenuAnchorEl)}
                            onClose={handleSurgicalClose}
                            disableRestoreFocus
                            disableScrollLock
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                            MenuListProps={{
                              onMouseEnter: clearMenuTimeout,
                              onMouseLeave: handleServicesMenuLeave,
                            }}
                            sx={{
                              pointerEvents: 'none',
                              '& .MuiPaper-root': {
                                pointerEvents: 'auto',
                                ml: 0,
                                minWidth: 280,
                                maxWidth: 350,
                                maxHeight: 400,
                                overflow: 'auto',
                                boxShadow: '0px 8px 25px rgba(0, 0, 0, 0.15)',
                                borderRadius: 2,
                              }
                            }}
                          >
                            {surgicalDepartments.length > 0 ? (
                              surgicalDepartments.map((dept) => (
                                <MenuItem
                                  key={dept.id}
                                  onClick={() => handleDepartmentClick(dept)}
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    '&:hover': {
                                      backgroundColor: 'rgba(37, 99, 235, 0.08)',
                                    }
                                  }}
                                >
                                  <ListItemText primary={dept.name} />
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem disabled>
                                <ListItemText primary="No surgical clinics available" />
                              </MenuItem>
                            )}
                          </Menu>
                        )}

                        {/* Nested Menu for Medical Clinics */}
                        {item.name === 'Medical Clinics' && item.hasSubmenu && (
                          <Menu
                            anchorEl={medicalSubmenuAnchorEl}
                            open={Boolean(medicalSubmenuAnchorEl)}
                            onClose={handleMedicalClose}
                            disableRestoreFocus
                            disableScrollLock
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                            MenuListProps={{
                              onMouseEnter: clearMenuTimeout,
                              onMouseLeave: handleServicesMenuLeave,
                            }}
                            sx={{
                              pointerEvents: 'none',
                              '& .MuiPaper-root': {
                                pointerEvents: 'auto',
                                ml: 0,
                                minWidth: 280,
                                maxWidth: 350,
                                maxHeight: 400,
                                overflow: 'auto',
                                boxShadow: '0px 8px 25px rgba(0, 0, 0, 0.15)',
                                borderRadius: 2,
                              }
                            }}
                          >
                            {medicalDepartments.length > 0 ? (
                              medicalDepartments.map((dept) => (
                                <MenuItem
                                  key={dept.id}
                                  onClick={() => handleDepartmentClick(dept)}
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    '&:hover': {
                                      backgroundColor: 'rgba(37, 99, 235, 0.08)',
                                    }
                                  }}
                                >
                                  <ListItemText primary={dept.name} />
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem disabled>
                                <ListItemText primary="No medical clinics available" />
                              </MenuItem>
                            )}
                          </Menu>
                        )}
                      </Box>
                    ))}
                  </Menu>
                </Box>

                {/* Services Dropdown */}
                <Box
                  onMouseEnter={handleNewServicesMouseEnter}
                  onMouseLeave={handleNewServicesMenuLeave}
                  sx={{ position: 'relative' }}
                >
                  <Button
                    sx={getDesktopNavButtonSx(isServicesNavActive())}
                    endIcon={<ExpandMoreIcon />}
                  >
                    Services
                  </Button>
                  
                  <Menu
                    anchorEl={newServicesAnchorEl}
                    open={Boolean(newServicesAnchorEl)}
                    onClose={handleNewServicesClose}
                    disableRestoreFocus
                    disableScrollLock
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    MenuListProps={{
                      onMouseEnter: handleNewServicesMenuEnter,
                      onMouseLeave: handleNewServicesMenuLeave,
                    }}
                    sx={{
                      pointerEvents: 'none',
                      '& .MuiPaper-root': {
                        pointerEvents: 'auto',
                        mt: 0.5,
                        minWidth: 250,
                        boxShadow: '0px 8px 25px rgba(0, 0, 0, 0.15)',
                        borderRadius: 2,
                      }
                    }}
                  >
                    {newServicesMenuItems.map((item) => (
                      <Box
                        key={item.name}
                        onMouseEnter={(e) => {
                          clearMenuTimeout();
                          if (item.name === 'OPD Services' && item.hasSubmenu) {
                            handleOpdMouseEnter(e);
                          } else if (item.name === 'IPD Services' && item.hasSubmenu) {
                            handleIpdMouseEnter(e);
                          } else if (item.name === 'Radiology' && item.hasSubmenu) {
                            handleRadiologyMouseEnter(e);
                          } else {
                            setOpdSubmenuAnchorEl(null);
                            setIpdSubmenuAnchorEl(null);
                            setRadiologySubmenuAnchorEl(null);
                          }
                        }}
                        sx={{ position: 'relative' }}
                      >
                        <MenuItem
                          component={item.path ? RouterLink : 'div'}
                          to={item.path || undefined}
                          onClick={() => {
                            if (!item.hasSubmenu && item.path) {
                              handleNewServicesItemClick();
                              navigate(item.path);
                            }
                          }}
                          sx={{
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                              backgroundColor: 'rgba(37, 99, 235, 0.08)',
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText primary={item.name} />
                          {item.hasSubmenu && (
                            <ChevronRightIcon sx={{ ml: 1, fontSize: 20 }} />
                          )}
                        </MenuItem>
                        
                        {/* Nested Menu for OPD Services */}
                        {item.name === 'OPD Services' && item.hasSubmenu && (
                          <Menu
                            anchorEl={opdSubmenuAnchorEl}
                            open={Boolean(opdSubmenuAnchorEl)}
                            onClose={handleOpdClose}
                            disableRestoreFocus
                            disableScrollLock
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                            MenuListProps={{
                              onMouseEnter: clearMenuTimeout,
                              onMouseLeave: handleNewServicesMenuLeave,
                            }}
                            sx={{
                              pointerEvents: 'none',
                              '& .MuiPaper-root': {
                                pointerEvents: 'auto',
                                ml: 0,
                                minWidth: 200,
                                boxShadow: '0px 8px 25px rgba(0, 0, 0, 0.15)',
                                borderRadius: 2,
                              }
                            }}
                          >
                            {item.submenuItems.map((subItem) => (
                              <MenuItem
                                key={subItem.name}
                                onClick={() => {
                                  handleNewServicesItemClick();
                                  if (subItem.path) {
                                    navigate(subItem.path);
                                  }
                                }}
                                sx={{
                                  py: 1.5,
                                  px: 2,
                                  '&:hover': {
                                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                                  }
                                }}
                              >
                                <ListItemText primary={subItem.name} />
                              </MenuItem>
                            ))}
                          </Menu>
                        )}

                        {/* Nested Menu for IPD Services */}
                        {item.name === 'IPD Services' && item.hasSubmenu && (
                          <Menu
                            anchorEl={ipdSubmenuAnchorEl}
                            open={Boolean(ipdSubmenuAnchorEl)}
                            onClose={handleIpdClose}
                            disableRestoreFocus
                            disableScrollLock
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                            MenuListProps={{
                              onMouseEnter: clearMenuTimeout,
                              onMouseLeave: handleNewServicesMenuLeave,
                            }}
                            sx={{
                              pointerEvents: 'none',
                              '& .MuiPaper-root': {
                                pointerEvents: 'auto',
                                ml: 0,
                                minWidth: 200,
                                boxShadow: '0px 8px 25px rgba(0, 0, 0, 0.15)',
                                borderRadius: 2,
                              }
                            }}
                          >
                            {item.submenuItems.map((subItem) => (
                              <MenuItem
                                key={subItem.name}
                                onClick={() => {
                                  handleNewServicesItemClick();
                                  if (subItem.path) {
                                    navigate(subItem.path);
                                  }
                                }}
                                sx={{
                                  py: 1.5,
                                  px: 2,
                                  '&:hover': {
                                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                                  }
                                }}
                              >
                                <ListItemText primary={subItem.name} />
                              </MenuItem>
                            ))}
                          </Menu>
                        )}

                        {/* Nested Menu for Radiology */}
                        {item.name === 'Radiology' && item.hasSubmenu && (
                          <Menu
                            anchorEl={radiologySubmenuAnchorEl}
                            open={Boolean(radiologySubmenuAnchorEl)}
                            onClose={handleRadiologyClose}
                            disableRestoreFocus
                            disableScrollLock
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                            MenuListProps={{
                              onMouseEnter: clearMenuTimeout,
                              onMouseLeave: handleNewServicesMenuLeave,
                            }}
                            sx={{
                              pointerEvents: 'none',
                              '& .MuiPaper-root': {
                                pointerEvents: 'auto',
                                ml: 0,
                                minWidth: 220,
                                boxShadow: '0px 8px 25px rgba(0, 0, 0, 0.15)',
                                borderRadius: 2,
                              }
                            }}
                          >
                            {item.submenuItems.map((subItem) => (
                              <MenuItem
                                key={subItem.name}
                                component={subItem.path ? RouterLink : 'div'}
                                to={subItem.path || undefined}
                                onClick={() => {
                                  handleNewServicesItemClick(subItem.path);
                                }}
                                sx={{
                                  py: 1.5,
                                  px: 2,
                                  '&:hover': {
                                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                                  }
                                }}
                              >
                                <ListItemText primary={subItem.name} />
                              </MenuItem>
                            ))}
                          </Menu>
                        )}
                      </Box>
                    ))}
                  </Menu>
                </Box>

                {/* About Us Dropdown */}
                <Box
                  onMouseEnter={handleAboutMouseEnter}
                  onMouseLeave={handleAboutMenuLeave}
                  sx={{ position: 'relative' }}
                >
                  <Button
                    sx={getDesktopNavButtonSx(isAboutNavActive())}
                    endIcon={<ExpandMoreIcon />}
                  >
                    About Us
                  </Button>
                  
                  <Menu
                    anchorEl={aboutAnchorEl}
                    open={Boolean(aboutAnchorEl)}
                    onClose={handleAboutClose}
                    disableRestoreFocus
                    disableScrollLock
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    MenuListProps={{
                      onMouseEnter: handleAboutMenuEnter,
                      onMouseLeave: handleAboutMenuLeave,
                    }}
                    sx={{
                      pointerEvents: 'none',
                      '& .MuiPaper-root': {
                        pointerEvents: 'auto',
                        mt: 0.5,
                        minWidth: 200,
                        boxShadow: '0px 8px 25px rgba(0, 0, 0, 0.15)',
                        borderRadius: 2,
                      }
                    }}
                  >
                    {aboutMenuItems.map((item) => (
                      <MenuItem
                        key={item.name}
                        component={RouterLink}
                        to={item.path}
                        onClick={handleAboutItemClick}
                        sx={{
                          py: 1.5,
                          px: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(37, 99, 235, 0.08)',
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.name} />
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>

                {/* Careers */}
                <Button
                  component={RouterLink}
                  to="/careers"
                  sx={getDesktopNavButtonSx(isCareersNavActive())}
                >
                  Careers
                </Button>

                {/* Contact */}
                <Button
                  component={RouterLink}
                  to="/contact"
                  sx={getDesktopNavButtonSx(isContactNavActive())}
                >
                  Contact
                </Button>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            boxShadow: '0px 8px 25px rgba(0,0,0,0.15)',
          },
        }}
      >
        {mobileDrawer}
      </Drawer>

      <PageTransitionLoader open={pageLoading} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          visibility: pageLoading ? 'hidden' : 'visible',
        }}
        aria-hidden={pageLoading}
      >
        <Outlet />
      </Box>

      <Box component="footer" sx={{ 
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        color: 'white',
        py: 10,
        mt: 'auto',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.5,
        },
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            flexWrap: 'wrap', 
            gap: 6,
            position: 'relative',
            zIndex: 2,
          }}>
            <Box sx={{ flex: 1, minWidth: 250 }}>
              <Box 
                component={RouterLink}
                to="/"
                sx={{ 
                  display: 'inline-flex', 
                  alignItems: 'center',
                  mb: 2,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  transform: 'translateY(0)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  }
                }}
              >
                <HospitalLogo size="medium" variant="footer" showText={true} clickable={false} />
              </Box>
              <Typography variant="body1" sx={{ 
                mb: 4, 
                opacity: 0.9, 
                lineHeight: 1.8,
                fontSize: '1.05rem',
                fontWeight: 300,
              }}>
                Providing exceptional healthcare services with compassion and excellence. 
                Your health and well-being are our top priorities at {hospitalInfo.name}.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  px: 2,
                  py: 1,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.2)',
                }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: '#10b981',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 },
                    },
                  }} />
                  <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    24/7 Emergency Care
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  px: 2,
                  py: 1,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.2)',
                }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: '#3b82f6',
                    animation: 'pulse 2s infinite',
                    animationDelay: '1s',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 },
                    },
                  }} />
                  <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    Expert Medical Team
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: 1, minWidth: 250 }}>
              <Typography variant="h6" gutterBottom sx={{ 
                fontWeight: 700, 
                mb: 4,
                fontSize: '1.3rem',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 40,
                  height: 3,
                  background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                  borderRadius: 2,
                }
              }}>
                Contact Info
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {hospitalInfo.showAddress && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 2,
                    p: 2,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'translateX(5px)',
                    }
                  }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      bgcolor: 'rgba(59, 130, 246, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      📍
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ 
                        opacity: 0.9, 
                        fontWeight: 500,
                        mb: 0.5
                      }}>
                        Address
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7, lineHeight: 1.5 }}>
                        {hospitalInfo.address}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {hospitalInfo.showContact && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 2,
                    p: 2,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'translateX(5px)',
                    }
                  }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      bgcolor: 'rgba(16, 185, 129, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      📞
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ 
                        opacity: 0.9, 
                        fontWeight: 500,
                        mb: 0.5
                      }}>
                        Phone
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        {hospitalInfo.contact}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {hospitalInfo.showEmail && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 2,
                    p: 2,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'translateX(5px)',
                    }
                  }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      bgcolor: 'rgba(139, 92, 246, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      ✉️
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ 
                        opacity: 0.9, 
                        fontWeight: 500,
                        mb: 0.5
                      }}>
                        Email
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        {hospitalInfo.email}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Typography variant="h6" gutterBottom sx={{ 
                fontWeight: 700, 
                mb: 4,
                fontSize: '1.3rem',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 40,
                  height: 3,
                  background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                  borderRadius: 2,
                }
              }}>
                Quick Links
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {/* Left Column */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    component={RouterLink}
                    to="/"
                    sx={{ 
                      justifyContent: 'flex-start', 
                      color: 'white', 
                      textTransform: 'none',
                      opacity: 0.8,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(5px)',
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    Home
                  </Button>
                  {/* <Button
                    component={RouterLink}
                    to="/doctors"
                    sx={{ 
                      justifyContent: 'flex-start', 
                      color: 'white', 
                      textTransform: 'none',
                      opacity: 0.8,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(5px)',
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    Doctors
                  </Button> */}
                  <Button
                    component={RouterLink}
                    to="/about/vision-mission"
                    sx={{ 
                      justifyContent: 'flex-start', 
                      color: 'white', 
                      textTransform: 'none',
                      opacity: 0.8,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(5px)',
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    Vision & Mission
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/about/ceo-message"
                    sx={{ 
                      justifyContent: 'flex-start', 
                      color: 'white', 
                      textTransform: 'none',
                      opacity: 0.8,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(5px)',
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    CEO Message
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/about/board"
                    sx={{ 
                      justifyContent: 'flex-start', 
                      color: 'white', 
                      textTransform: 'none',
                      opacity: 0.8,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(5px)',
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    Board of Directors
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/about/team"
                    sx={{ 
                      justifyContent: 'flex-start', 
                      color: 'white', 
                      textTransform: 'none',
                      opacity: 0.8,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(5px)',
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    Our Team
                  </Button>
                </Box>
                
                {/* Right Column */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    component={RouterLink}
                    to="/services/medical-clinics"
                    sx={{ 
                      justifyContent: 'flex-start', 
                      color: 'white', 
                      textTransform: 'none',
                      opacity: 0.8,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(5px)',
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    Medical Clinics
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/services/surgical-clinics"
                    sx={{ 
                      justifyContent: 'flex-start', 
                      color: 'white', 
                      textTransform: 'none',
                      opacity: 0.8,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(5px)',
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    Surgical Clinics
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/services/diagnostic-services"
                    sx={{ 
                      justifyContent: 'flex-start', 
                      color: 'white', 
                      textTransform: 'none',
                      opacity: 0.8,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(5px)',
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    Diagnostic Services
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/careers"
                    sx={{ 
                      justifyContent: 'flex-start', 
                      color: 'white', 
                      textTransform: 'none',
                      opacity: 0.8,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(5px)',
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    Careers
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/departments"
                    sx={{ 
                      justifyContent: 'flex-start', 
                      color: 'white', 
                      textTransform: 'none',
                      opacity: 0.8,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(5px)',
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    Departments
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/contact"
                    sx={{ 
                      justifyContent: 'flex-start', 
                      color: 'white', 
                      textTransform: 'none',
                      opacity: 0.8,
                      py: 1.5,
                      px: 2,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateX(5px)',
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    Contact Us
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={{ 
            borderTop: '1px solid rgba(255, 255, 255, 0.1)', 
            mt: 8, 
            pt: 6, 
            textAlign: 'center',
            position: 'relative',
            zIndex: 2,
          }}>
            <Typography variant="body2" sx={{ 
              opacity: 0.7,
              fontSize: '0.95rem',
              fontWeight: 300,
            }}>
              © 2024 {hospitalInfo.name}. All rights reserved.
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 3, 
              mt: 3,
              flexWrap: 'wrap'
            }}>
              <Typography variant="body2" sx={{ 
                opacity: 0.6,
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'opacity 0.3s ease',
                '&:hover': { opacity: 0.9 }
              }}>
                Privacy Policy
              </Typography>
              <Typography variant="body2" sx={{ 
                opacity: 0.6,
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'opacity 0.3s ease',
                '&:hover': { opacity: 0.9 }
              }}>
                Terms of Service
              </Typography>
              <Typography variant="body2" sx={{ 
                opacity: 0.6,
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'opacity 0.3s ease',
                '&:hover': { opacity: 0.9 }
              }}>
                Cookie Policy
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

const PublicLayout = () => {
  const { state } = useData();
  return (
    <PageReadyProvider initialLoadComplete={state.initialLoadComplete}>
      <PublicLayoutContent />
    </PageReadyProvider>
  );
};

export default PublicLayout;